import { Inject, Injectable } from '@angular/core';

import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import {
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
  startWith,
  switchMap
} from 'rxjs';

import { Category, GlobalProductsRoutes, GlobalSearchRoutes } from '@hdms/shared/common-types';
import { GQL_DATASOURCE_VERSION_HEADER, MPIS, UserContextService, getLocalization } from '@hdms/shared/data-access';
import {
  EditorialResponseLink,
  PrimaryMenuType,
  PrimaryNavItem,
  ProductMenuType,
  SecondaryNavGroup,
  SecondaryNavItem
} from '@hdms/shared/page-frame/types';
import {
  AssetPathPipe,
  EnvironmentService,
  GQL_RESPONSE_CACHE_TTL_HEADER,
  RouterHelperService,
  TokenService
} from '@hdms/shared/util-core';

import { AemNavigationService } from './aem-navigation.service';
import { HeaderProductCategoriesGqlService } from './header-product-categories-gql.service';

// 4h cache TTL for categories GQL response
const CATEGORIES_CACHE_TTL = 14400;

const ProductPath = {
  BASE: `/${GlobalProductsRoutes.BASE}/`,
  CATEGORY: `/${GlobalProductsRoutes.BASE_CATEGORY_PAGE}`,
  SPECIAL: `/${GlobalSearchRoutes.SPECIAL_PRODUCTS}`
};

/**
 * Extract all child urls from applied nested Editorial section into flat array.
 * In order to match additional subpages when comparing the urls with current path,
 * remove .html path extension from urls, as last path fragment can also be a folder.
 * @param links
 */
function filterChildUrls(links: EditorialResponseLink[]): string[] {
  return links.flatMap(({ childList, url }) => (childList ? filterChildUrls(childList) : url?.replace(/.html$/, '')));
}

function addCategoryUrls(categories: Required<Category>[]): SecondaryNavItem[] {
  return categories.map(category => ({
    ...category,
    url: `${ProductPath.BASE}${category.id}`
  }));
}

@Injectable({
  providedIn: 'root'
})
export class HeaderNavigationService {
  public readonly primaryLinks$ = this.getPrimaryNavLinks$();

  constructor(
    private readonly env: EnvironmentService,
    private readonly tokenService: TokenService,
    @Inject(I18NEXT_SERVICE) private readonly i18next: ITranslationService,
    private readonly categoriesGqlService: HeaderProductCategoriesGqlService,
    private readonly routerHelper: RouterHelperService,
    private readonly userContextService: UserContextService,
    private readonly assetPathPipe: AssetPathPipe,
    private readonly aemNavigation: AemNavigationService
  ) {}

  private getPrimaryNavLinks$(): Observable<PrimaryNavItem[]> {
    return combineLatest([this.getProductNavItem$(), this.getEditorialNavItems$()]).pipe(
      map(([productLink, editorialLinks]) => [productLink, ...editorialLinks]),
      shareReplay(1)
    );
  }

  private getProductNavItem$(): Observable<PrimaryNavItem> {
    return (
      this.tokenService.authToken || this.env.hostContext ? this.getCategories$() : of({} as SecondaryNavGroup)
    ).pipe(
      map(categories => ({
        id: 'p0',
        title: this.i18next.t('header.navigation.products'),
        menuType: PrimaryMenuType.PRODUCTS,
        url: ProductPath.CATEGORY,
        active: this.linkIsActive(Object.values(ProductPath)),
        childList: [categories]
      }))
    );
  }

  /**
   * Receive categories from GraphQL endpoint.
   * By adding internal cache header the response data will be cached in Redis,
   * so it can be reused for all pages per country/language.
   * @private
   */
  private getCategories$(): Observable<SecondaryNavGroup> {
    return this.categoriesGqlService
      .fetch(
        { localization: getLocalization(this.env) },
        {
          context: {
            headers: {
              [GQL_RESPONSE_CACHE_TTL_HEADER]: String(CATEGORIES_CACHE_TTL),
              [GQL_DATASOURCE_VERSION_HEADER]: MPIS
            }
          }
        }
      )
      .pipe(
        map(({ data: { topLevelProductNavigation } }) => topLevelProductNavigation),
        catchError(_ => of([])),
        map(addCategoryUrls),
        switchMap(categories => this.getAccountManagerCategories$(categories)),
        map(childList => ({
          menuType: ProductMenuType.CATEGORY,
          childList
        }))
      );
  }

  private getEditorialNavItems$(): Observable<PrimaryNavItem[]> {
    return this.aemNavigation.editorialLinks$.pipe(map(links => [...this.parseEditorialLinks(links)]));
  }

  private parseEditorialLinks(links: EditorialResponseLink[]): PrimaryNavItem[] {
    return links.map((group, index) => ({
      ...group,
      childList: group.childList?.map(secondaryGroup => ({
        ...secondaryGroup,
        childList: secondaryGroup.childList?.map(({ image, ...rest }) => ({
          ...rest,
          thumbnail: image
        }))
      })),
      id: `e${index}`,
      menuType: PrimaryMenuType.EDITORIAL,
      active: this.linkIsActive(filterChildUrls([group]))
    }));
  }

  private getAccountManagerCategories$(categories: SecondaryNavItem[]): Observable<SecondaryNavItem[]> {
    return this.userContextService.update$().pipe(
      startWith(null),
      map(_ => this.userContextService.isAccountManager),
      distinctUntilChanged(),
      map(isAm => this.addSpecialProductsNavLink(categories, isAm))
    );
  }

  private addSpecialProductsNavLink(categories: SecondaryNavItem[], isAccountManager: boolean): SecondaryNavItem[] {
    return isAccountManager ? [...categories, this.getSpecialProductsNavLink()] : categories;
  }

  private getSpecialProductsNavLink(): SecondaryNavItem {
    return {
      title: this.i18next.t('header.navigation.specialProducts'),
      thumbnail: this.assetPathPipe.transform('/assets/images/navigation/icon-category-special-products.png'),
      url: ProductPath.SPECIAL
    };
  }

  private linkIsActive(linkUrls: string[]): boolean {
    const finalUrl = this.routerHelper.finalUrl;
    return linkUrls.some(url => finalUrl.startsWith(url));
  }
}
